import { Pressable, View } from "react-native";

import { Text } from "@/components/ui/text";
import { cn } from "@/lib/utils";

import { useTranslation } from "react-i18next";
import { i18n } from "../../i18n";
import type { SelectOption } from "./enum-select";
import { SettingsChevron } from "./settings";

const SUMMARY_VISIBLE_COUNT = 2;

type SetSelectProps = {
    options: SelectOption[];
    value: number[];
    placeholder?: string;
    onPress: () => void;
    testID?: string;
};

function isNoneOption(option: SelectOption): boolean {
    return (
        option.label.trim().toUpperCase() === "NONE" ||
        option.label.trim().toUpperCase() === "ŽÁDNÉ"
    );
}

function findNoneOptionId(options: SelectOption[]): number | undefined {
    return options.find(isNoneOption)?.id;
}

export function buildSetSelectionSummary(
    options: SelectOption[],
    value: number[],
    placeholder: string,
): string {
    if (value.length === 0) {
        return placeholder;
    }

    const selectedLabels = options
        .filter((option) => value.includes(option.id))
        .map((option) => option.label);

    if (selectedLabels.length === 0) {
        return placeholder;
    }

    if (selectedLabels.length <= SUMMARY_VISIBLE_COUNT) {
        return selectedLabels.join(", ");
    }

    const visible = selectedLabels.slice(0, SUMMARY_VISIBLE_COUNT).join(", ");
    return i18n.t("ui.selectOptionsMore", {
        visible,
        count: selectedLabels.length - SUMMARY_VISIBLE_COUNT,
    });
}

export function toggleSetSelection(
    options: SelectOption[],
    value: number[],
    optionId: number,
    checked: boolean,
): number[] {
    const option = options.find((item) => item.id === optionId);
    if (!option) {
        return value;
    }

    if (checked) {
        if (isNoneOption(option)) {
            return [optionId];
        }

        const noneOptionId = findNoneOptionId(options);
        const withoutNone =
            noneOptionId == null
                ? value
                : value.filter((id) => id !== noneOptionId);
        if (withoutNone.includes(optionId)) {
            return withoutNone;
        }
        return [...withoutNone, optionId];
    }

    return value.filter((id) => id !== optionId);
}

/**
 * Summary row for set-type fields. Tapping opens a dedicated selection screen
 * with a checklist of all options.
 */
export function SetSelect({
    options,
    value,
    placeholder,
    onPress,
    testID,
}: SetSelectProps) {
    const { t } = useTranslation();
    const tPlaceholder = placeholder ?? t("ui.selectOptions");
    const summary = buildSetSelectionSummary(options, value, tPlaceholder);
    const hasSelection = value.length > 0;

    return (
        <View className="w-full">
            <Pressable
                accessibilityRole="button"
                className={cn("flex-row items-center justify-between pr-2")}
                onPress={onPress}
            >
                <Text
                    testID={testID}
                    className={cn(
                        hasSelection
                            ? "text-foreground"
                            : "text-muted-foreground",
                    )}
                >
                    {summary}
                </Text>
                <SettingsChevron />
            </Pressable>
        </View>
    );
}
